<template>
    <div>
        <b-container v-if="!ready">
            <b-row class="vh-100 text-center" align-v="center">
                <b-col>
                    <b-spinner variant="light" />
                </b-col>
            </b-row>
        </b-container>
        <TemplateEditor 
            v-model="nodes" 
            @save="save" 
            :saving="saving" 
            :builderConnected="builderConnected"
            :builderPort="builderPort"
            @update:builderPort="builderPort = $event"
            :showBuilderSettings="showBuilderSettings"
            @update:showBuilderSettings="showBuilderSettings = $event"
            @connectToBuilder="connectToBuilder"
            v-else
        >
            <template #nav>
                <b-nav-item-dropdown text="Files" no-caret>
                    <b-dropdown-item @click="saveAll">Save All</b-dropdown-item>
                    <b-dropdown-item @click="saveAllAndBuild">Save All and Build</b-dropdown-item>
                </b-nav-item-dropdown>

                <div class="vscode-builder-status ml-3">
                    <span class="vscode-builder-label">Builder</span>
                    <div 
                        :class="['vscode-builder-indicator', { 'vscode-builder-connected': builderConnected }]"
                        v-b-tooltip.hover 
                        :title="builderConnected ? `Connected to port ${builderPort}` : 'Disconnected'"
                    ></div>
                    <b-icon 
                        icon="gear" 
                        class="vscode-builder-settings"
                        @click="showBuilderSettings = true"
                        v-b-tooltip.hover 
                        title="Builder Settings"
                    ></b-icon>
                    <b-spinner v-if="saving" small variant="light" class="vscode-builder-spinner" />
                    <span class="vscode-builder-info">{{ SDKVersion ? `SDK ${SDKVersion}` : 'SDK: Not Set' }}</span>
                    <span class="vscode-builder-info">{{ SDKEngine ? `Engine: ${SDKEngine}` : 'Engine: Not Set' }}</span>
                </div>
            </template>
        </TemplateEditor>

        <!-- Builder Settings Modal -->
        <main-dialog
            v-model="showBuilderSettings"
            title="Builder Settings"
            header-bg-variant="dark"
            body-bg-variant="dark"
            footer-bg-variant="dark"
            @ok="connectToBuilder"
        >
            <b-form-group label="Builder Port" label-for="builder-port">
                <b-form-input
                    id="builder-port"
                    v-model="builderPort"
                    type="number"
                    placeholder="11403"
                ></b-form-input>
            </b-form-group>
            <div class="mt-2">
                <small class="text-muted">
                    Current status: 
                    <span :class="builderConnected ? 'text-success' : 'text-danger'">
                        {{ builderConnected ? `Connected to port ${builderPort}` : 'Disconnected' }}
                    </span>
                </small>
            </div>
        </main-dialog>

        <!-- Notification Panel -->
        <div v-if="notifications.length > 0" class="vscode-notifications">
            <div 
                v-for="notification in notifications" 
                :key="notification.id"
                :class="['vscode-notification', `vscode-notification-${notification.type}`]"
            >
                <div class="vscode-notification-icon">
                    <b-icon :icon="notification.type === 'error' ? 'exclamation-circle-fill' : notification.type === 'warning' ? 'exclamation-triangle-fill' : 'check-circle-fill'"></b-icon>
                </div>
                <div class="vscode-notification-content">
                    <div class="vscode-notification-message">{{ notification.message }}</div>
                </div>
                <div class="vscode-notification-close" @click="dismissNotification(notification.id)">
                    <b-icon icon="x"></b-icon>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TemplateEditor from '../editor/TemplateEditor.vue'
import _ from 'lodash'
import Vue from 'vue'
import { TreeNode } from 'vue-tree-list'

export default {
    name: 'EditorV5',
    components: {
        TemplateEditor
    },
    data: function() {
        return {
            component: null,
            nodes: null,
            saving: false,
            ready: false,
            SDKVersion: null,
            SDKEngine: null,
            builderConnected: false,
            builderPort: 11403,
            showBuilderSettings: false,
            builderCheckInterval: null,
            notifications: [],
            notificationIdCounter: 0
        }
    },
    computed: {
        settings() {
            return this.component.settings
        },
    },
    watch: {
        data: function(o, n) {
            console.log(n)
        }
    },
    methods: {
        showNotification(message, type = 'info', duration = 5000) {
            const id = this.notificationIdCounter++;
            this.notifications.push({ id, message, type });
            
            if (duration > 0) {
                setTimeout(() => {
                    this.dismissNotification(id);
                }, duration);
            }
        },

        dismissNotification(id) {
            this.notifications = this.notifications.filter(n => n.id !== id);
        },

        build(nodes) {
            return new Promise((resolve, reject) => {
                const ws = new WebSocket(`ws://localhost:${this.builderPort}`)
                ws.onopen = () => {
                    console.log('WebSocket connection established to Builder', nodes)
                    ws.send(JSON.stringify(nodes))
                }
                
                ws.onmessage = (e) => {
                    const data = JSON.parse(e.data)
                    const { dist, sdk_version, sdk_engine, error, warnings } = data

                    console.log('Received build response from Builder', data)
                    
                    if (error) {
                        reject(new Error(error.message))

                        this.showNotification(error, 'error');
                        
                        return
                    }

                    // Display warnings if present
                    if (warnings && warnings.length > 0) {
                        warnings.forEach(warning => {
                            this.showNotification(warning, 'warning');
                        });
                    }

                    resolve({ dist, sdk_version, sdk_engine })

                    ws.close()
                }

                ws.onerror = (e) => {
                    reject(e)

                    this.showNotification('Failed to connect to builder', 'error');

                    ws.close()
                }
            })

           
        },

        async checkVersionMismatch(builderSDK, builderEngine) {
            const savedSDK = this.component && this.component.settings && this.component.settings.templates_dist && this.component.settings.templates_dist.sdk_version;
            const savedEngine = this.component && this.component.settings && this.component.settings.templates_dist && this.component.settings.templates_dist.sdk_engine;

            // If no saved version exists, no need to confirm
            if (!savedSDK || !savedEngine) {
                return true;
            }

            // Convert both to integers for comparison
            const builderSDKInt = parseInt(builderSDK, 10);
            const savedSDKInt = parseInt(savedSDK, 10);

            // Check if there's a mismatch
            if (builderSDKInt !== savedSDKInt || builderEngine !== savedEngine) {
                const message = `SDK/Engine version mismatch detected:\n\n` +
                    `Last saved: SDK ${savedSDK}, Engine: ${savedEngine}\n` +
                    `Builder provides: SDK ${builderSDK}, Engine: ${builderEngine}\n\n` +
                    `Are you sure you want to build with the new version?`;

                return await this.$mainDialog.msgBoxConfirm(message, {
                    title: 'Version Mismatch',
                    headerBgVariant: 'dark',
                    bodyBgVariant: 'dark',
                    footerBgVariant: 'dark',
                    okTitle: 'Yes, Build',
                    cancelTitle: 'Cancel'
                });
            }

            return true;
        },

        save: _.debounce(async function() {
            this.saving = true;
            try {
                const templates = JSON.parse(this.nodes[0].children[0].toString())
                const results = await this.build(templates.children)

                // Check for version mismatch before proceeding
                const shouldContinue = await this.checkVersionMismatch(results.sdk_version, results.sdk_engine);
                if (!shouldContinue) {
                    this.saving = false;
                    return;
                }

                // Update SDK version and engine from builder response
                if (results.sdk_version) {
                    this.SDKVersion = parseInt(results.sdk_version, 10);
                }
                if (results.sdk_engine) {
                    this.SDKEngine = results.sdk_engine;
                }

                const payloadToSave = {
                    nodes: templates.children,
                    templates_dist: {
                        sdk_version: parseInt(this.SDKVersion, 10),
                        sdk_engine: this.SDKEngine,
                        dist: results.dist
                    }
                };
                
                console.log('Saving payload:', payloadToSave)
                console.log('Current component templates_dist before save:', this.component.settings.templates_dist)

                this.$http.post(`/v3/system/component/templates/${this.component.general.uuid}/${this.settings.version}`, payloadToSave).then(() => {
                    this.showNotification('Saved successfully', 'success', 3000);
                }).catch(err => {
                    this.$debugLog(err)

                    if (err.response) {
                        this.showNotification(err.response.data.message, 'error');

                        return
                    }

                    this.showNotification(err.message, 'error');
                    
                })

            } catch (err) {
                this.showNotification(err.message, 'error');
            } finally {
                this.saving = false
            }

            
        }, 500),

        async saveAll() {
            this.saving = true;
            try {
                const templates = JSON.parse(this.nodes[0].children[0].toString() || [])

                await this.$http.post(`/v3/system/component/templates/${this.component.general.uuid}/${this.settings.version}`, {
                    nodes: templates.children,
                    templates_dist: {
                        sdk_version: parseInt(this.SDKVersion, 10),
                        sdk_engine: this.SDKEngine,
                        ...(this.component && this.component.settings && this.component.settings.templates_dist || {})
                    }
                })

                this.showNotification('Saved successfully', 'success', 3000);
            } catch (err) {
                if (err.message.includes('[object Object]')) {
                    return
                }

                this.$debugLog(err)

                if (err.response) {
                    this.showNotification(err.response.data.message, 'error');
                    return
                }

                this.showNotification(err.message, 'error');
            } finally {
                this.saving = false
            }
        },

        async saveAllAndBuild() {
            this.saving = true;
            try {
                const templates = JSON.parse(this.nodes[0].children[0].toString() || [])
                const results = await this.build(templates.children)

                // Check for version mismatch before proceeding
                const shouldContinue = await this.checkVersionMismatch(results.sdk_version, results.sdk_engine);
                if (!shouldContinue) {
                    this.saving = false;
                    return;
                }

                // Update SDK version and engine from builder response
                if (results.sdk_version) {
                    this.SDKVersion = results.sdk_version;
                }
                if (results.sdk_engine) {
                    this.SDKEngine = results.sdk_engine;
                }

                console.log('Build results:', results)

                await this.$http.post(`/v3/system/component/templates/${this.component.general.uuid}/${this.settings.version}`, {
                    nodes: templates.children,
                    templates_dist: {
                        sdk_version: parseInt(this.SDKVersion, 10),
                        sdk_engine: this.SDKEngine,
                        dist: results.dist
                    }
                })

                this.showNotification('Saved and built successfully', 'success', 3000);
            } catch (err) {
                if (err.message.includes('[object Object]')) {
                    return
                }

                this.$debugLog(err)

                if (err.response) {
                    this.showNotification(err.response.data.message, 'error');
                    return
                }

                this.showNotification(err.message, 'error');
            } finally {
                this.saving = false
            }
        },

        getBuilder() {
            return null
            //return new Builder(this.SDKVersion, this.SDKEngine)
        },

        async onDel(node) {
            let resp = await this.$mainDialog.msgBoxConfirm("Are you sure you want to delete " + node.name + "?", {
                headerBgVariant: "dark",
                bodyBgVariant: "dark",
                footerBgVariant: "dark"
            });
            if (!resp) return;
            
            node.remove()
        },

        onChangeName(node) {
            if (node.node) {
                // decide extension of name
                node.node.path = this.generateNodePath(node.node, node.newName)
                // set ext of the file
                node.node.ext = this.resolveFileExt(node.newName)
                node.node.editorOptions.mode = this.resolveEditorMode(node.node.ext)
                // find active editor and change mode of editor depending on ext
                let activeEditor = this.findNodeByID(node.id, this.activeEditors)
                if (activeEditor) {
                    activeEditor = node.node
                }
            }
        },    

        resolveFileExt(name) {
            let parts = name.split(".")
            return parts[parts.length - 1]
        },

        resolveEditorMode(ext) {
            switch(ext) {
                case 'vue': return "text/x-vue";
                case 'js': return "application/javascript";
                default: return "text/plain";
            }
        },

        onClick(params) {
            if (!params.isLeaf) return;

            params.path = this.generateNodePath(params)

            let found = this.activeEditors.find(e => {
                return e.id == params.id
            })

            if (!found) 
                this.activeEditors.push(params)
        },

        addNode(node) {
            if (!node.isLeaf) return;

            node.path = this.generateNodePath(node, node.name)
            node.editorOptions = {
                mode: this.resolveEditorMode(this.resolveFileExt(node.name))
            }
        },

        generateNodePath(n,p) {
            let path = p
            
            if (n && n.parent) {
                path = n.parent.name + "/" + path
            
                return this.generateNodePath(n.parent, path)
            }
            
            return path.replaceAll("root/" + this.component.general.name + "/Templates/", "")
        },

        editorOptions(override) {
            return {
                tabSize: 2,
                line: true,
                autoRefresh: true,
                lineNumbers: true,
                foldGutter: true,
                firstLineNumber: 1,
                styleSelectedText: true,
                matchBrackets: true,
                showCursorWhenSelecting: true,
                theme: 'material-palenight',
                ...override
            }
        },

        editorInput: _.debounce(function(activeEditor, val) {
            let node = this.findNodeByID(activeEditor.id)
            
            node.content = val
        }, 500),

        currentEditorValue(activeEditor) {
            return activeEditor.content || ""
        },

        findNodeByID(id, nodes) {
            if (!nodes) {
                nodes = this.data.children
            }

            let node = null
            for (let i = 0; i <= nodes.length; i++) {
                if (!nodes[i]) continue;

                if (nodes[i].id == id) {
                    node = nodes[i]
                    break
                }

                node = this.findNodeByID(id, nodes[i].children || [])
            }

            return node
        },

        closeActiveEditor(editor) {
            if (this.activeEditors.length < 2) return;

            this.activeEditors = this.activeEditors.filter(e => {
                return e.id != editor.id
            })
        },

        setMenu(top, left) {     
            let largestHeight = window.innerHeight - this.$refs.right.offsetHeight - 25;
            let largestWidth = window.innerWidth - this.$refs.right.offsetWidth - 25;

            if (top > largestHeight) top = largestHeight;

            if (left > largestWidth) left = largestWidth;

            this.top = top + 'px';
            this.left = left + 'px';
        },

        closeMenu() {
            this.viewMenu = false;
        },

        openMenu(e, node) {
            e.preventDefault();

            this.currentMenuNode = node;

            if (this.nodeActions().length < 1) {
                return
            }

            this.viewMenu = true;

            Vue.nextTick(function() {
                this.$refs.right.focus();

                this.setMenu(e.y, e.x)
            }.bind(this));
        },

        triggerNodeAction(action) {
            switch(action) {
                case 'Delete':
                    this.removeNode(this.currentMenuNode)
                    break
                case 'Add file':
                    this.addFile(this.currentMenuNode)
                    break
                case 'Add folder':
                    this.addFolder(this.currentMenuNode)
                    break
                case 'Rename':
                    this.renameNode(this.currentMenuNode)
                    break
            }

            this.closeMenu()
        },

        renameNode(node) {
            if (node.editNodeDisabled) {
                return
            }

            this.nodeEditable = node.id
            Vue.nextTick(function() {
                this.$refs.renameInput.focus();
            }.bind(this));
        },

        removeNode(node) {
            if (node.delNodeDisabled) {
                return
            }

            this.$mainDialog.msgBoxConfirm("Are you sure?",{
                headerBgVariant: 'dark',
                footerBgVariant: 'dark',
                bodyBgVariant: 'dark'
            }).then(resp => {
                if (resp) node.remove()
            })  
        },
        
        addFile(node) {
            node.addChildren(new TreeNode(
                {
                    name: 'new file',
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: true,
                    addLeafNodeDisabled: true,
                    delNodeDisabled: false,
                    editNodeDisabled: false,
                    children: null,
                    editorOptions: {
                        mode: 'text/plain'
                    }
                }
            ))
        },

        addFolder(node) {
            node.addChildren(new TreeNode(
                {
                    name: 'new folder',
                    isLeaf: false,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: false,
                    editNodeDisabled: false,
                    children: []
                }
            ))
        },

        nodeActions() {
            let actions = []
            let node = this.currentMenuNode;
            if (!node.isLeaf) {
                actions.push
                if (!node.addLeafNodeDisabled) {
                    actions.push("Add file")
                }

                if (!node.addTreeNodeDisabled) {
                    actions.push("Add folder")
                }
            }

            if (!node.delNodeDisabled) {
                actions.push("Delete")
            }

            if (!node.editNodeDisabled) {
                actions.push("Rename")
            }

            return actions;
        },

        nodeNameChange() {
            this.currentMenuNode.changeName(this.currentMenuNode.name)
            this.nodeEditable = null
            this.onChangeName({newName: this.currentMenuNode.name, node: this.currentMenuNode})
        },

        onSidebarResize(x, y, width) {
            this.$refs.editorPanel.style.marginLeft = width + "px"
        },

        checkBuilderConnection() {
            console.log(`Attempting to connect to Builder on port ${this.builderPort}...`);
            return new Promise((resolve) => {
                const socket = new WebSocket(`ws://localhost:${this.builderPort}/`);
                const timeout = setTimeout(() => {
                    socket.close();
                    this.builderConnected = false;
                    console.log(`✗ Builder connection timed out on port ${this.builderPort}`);
                    resolve();
                }, 3000);

                socket.onopen = () => {
                    clearTimeout(timeout);
                    socket.close();
                    this.builderConnected = true;
                    console.log(`✓ Connected to Builder on port ${this.builderPort}`);
                    resolve();
                };

                socket.onerror = () => {
                    clearTimeout(timeout);
                    this.builderConnected = false;
                    console.log(`✗ Failed to connect to Builder on port ${this.builderPort}`);
                    resolve();
                };
            });
        },

        connectToBuilder() {
            console.log('connectToBuilder called');
            // Save the port to localStorage
            localStorage.setItem('builderPort', this.builderPort);
            
            // Clear existing interval
            if (this.builderCheckInterval) {
                clearInterval(this.builderCheckInterval);
            }
            
            // Check connection immediately (don't await, let it run in background)
            this.checkBuilderConnection().catch(err => {
                console.error('Builder connection check error:', err);
            });
            
            // Set up interval to check connection every 5 seconds
            this.builderCheckInterval = setInterval(() => {
                this.checkBuilderConnection().catch(err => {
                    console.error('Builder connection check error:', err);
                });
            }, 5000);
            console.log('Builder check interval set up');
        },

        loadBuilderSettings() {
            console.log('loadBuilderSettings called');
            const savedPort = localStorage.getItem('builderPort');
            if (savedPort) {
                this.builderPort = parseInt(savedPort, 10);
            }
            console.log('Builder port set to:', this.builderPort);
            
            // Start checking connection (non-blocking)
            this.connectToBuilder();
        }
    },

    async mounted() {
        try {
            let { data } = await this.$http.get("/v3/system/component/settings/" + this.$route.params.id + "/" + this.$route.query.v)

            this.component = data
            
            console.log('Component data:', data)
            console.log('Templates dist:', data.settings && data.settings.templates_dist)
            console.log('Templates dist SDK version:', data.settings && data.settings.templates_dist && data.settings.templates_dist.sdk_version)
            console.log('Templates dist SDK engine:', data.settings && data.settings.templates_dist && data.settings.templates_dist.sdk_engine)

            // Build base nodes array
            this.nodes = [
                {
                    name: this.component.general.name,
                    id: 1,
                    pid: 0,
                    root: true,
                    dragDisabled: true,
                    editNodeDisabled: true,
                    delNodeDisabled: true,
                    addTreeNodeDisabled: true,
                    addLeafNodeDisabled: true,
                    children: [
                        {
                            name: 'templates',
                            id: 2,
                            pid: 1,
                            dragDisabled: true,
                            addTreeNodeDisabled: false,
                            addLeafNodeDisabled: false,
                            delNodeDisabled: true,
                            editNodeDisabled: true,
                            children: this.component && this.component.settings && this.component.settings.templates || []

                        }
                    ]
                }
            ]

            // Only add extensions node if there are extensions
            if (this.component && this.component.settings && this.component.settings.extensions && this.component.settings.extensions.length > 0) {
                this.nodes.push({
                    name: "extensions",
                    id: 1,
                    pid: 0,
                    root: true,
                    dragDisabled: true,
                    editNodeDisabled: true,
                    delNodeDisabled: true,
                    addTreeNodeDisabled: true,
                    addLeafNodeDisabled: true,
                    children: this.component.settings.extensions.map(ext => {
                        return {
                                name: ext.name,
                                id: 3,
                                pid: 2,
                                dragDisabled: true,
                                addTreeNodeDisabled: false,
                                addLeafNodeDisabled: false,
                                delNodeDisabled: true,
                                editNodeDisabled: true,
                                children: ext.templates || []
                            }
                    })
                })
            }

            if (this.component && this.component.settings && this.component.settings.templates_dist) {
                console.log('Setting SDKVersion and SDKEngine from templates_dist')
                console.log('Before - SDKVersion:', this.SDKVersion, 'SDKEngine:', this.SDKEngine)
                this.SDKVersion = this.component.settings.templates_dist.sdk_version != null ? this.component.settings.templates_dist.sdk_version : null
                this.SDKEngine = this.component.settings.templates_dist.sdk_engine != null ? this.component.settings.templates_dist.sdk_engine : null
                console.log('After - SDKVersion:', this.SDKVersion, 'SDKEngine:', this.SDKEngine)
            } else {
                console.log('No templates_dist found in component settings')
            } 

            this.ready = true

            // Initialize builder connection check
            this.loadBuilderSettings();

       }  catch(err) {
            this.$debugLog(err)
            this.$mainDialog.msgBoxOk("Failed to fetch component settings.")
        }

        document.onkeyup = function(e) {
            if (e.ctrlKey && e.code == 'KeyS') {
                this.save()
            }
        }.bind(this);
    },

    beforeDestroy() {
        // Clean up the builder check interval
        if (this.builderCheckInterval) {
            clearInterval(this.builderCheckInterval);
        }
    }
}
</script>

<style scoped>
.vscode-builder-status {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
    font-size: 12px;
}

.vscode-builder-label {
    color: #858585;
    font-weight: 500;
}

.vscode-builder-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #f44336;
    box-shadow: 0 0 4px rgba(244, 67, 54, 0.5);
    transition: all 0.3s ease;
}

.vscode-builder-indicator.vscode-builder-connected {
    background: #4caf50;
    box-shadow: 0 0 8px rgba(76, 175, 80, 0.8);
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.6;
    }
}

.vscode-builder-settings {
    color: #858585;
    cursor: pointer;
    font-size: 14px;
    transition: color 0.1s ease;
}

.vscode-builder-settings:hover {
    color: #cccccc;
}

.vscode-builder-spinner {
    margin: 0 8px;
}

.vscode-builder-info {
    display: inline-block;
    color: #cccccc;
    padding: 0 8px;
    font-size: 12px;
    cursor: default;
}

::v-deep .dropdown-item::marker {
    display: none !important;
}

::v-deep .dropdown-item {
    list-style: none !important;
    padding-left: 1rem !important;
}

::v-deep .dropdown-menu {
    list-style: none !important;
}

/* Notification Panel */
.vscode-notifications {
    position: fixed;
    bottom: 30px;
    right: 20px;
    z-index: 9999;
    max-width: 450px;
    min-width: 350px;
}

.vscode-notification {
    display: flex;
    align-items: flex-start;
    background: #252526;
    border: 1px solid #3c3c3c;
    border-left: 4px solid;
    margin-bottom: 10px;
    padding: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    animation: slideIn 0.3s ease;
}

@keyframes slideIn {
    from {
        transform: translateX(100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

.vscode-notification-error {
    border-left-color: #f44336;
}

.vscode-notification-success {
    border-left-color: #4caf50;
}

.vscode-notification-warning {
    border-left-color: #ff9800;
}

.vscode-notification-info {
    border-left-color: #007acc;
}

.vscode-notification-icon {
    margin-right: 12px;
    font-size: 18px;
    flex-shrink: 0;
}

.vscode-notification-error .vscode-notification-icon {
    color: #f44336;
}

.vscode-notification-success .vscode-notification-icon {
    color: #4caf50;
}

.vscode-notification-warning .vscode-notification-icon {
    color: #ff9800;
}

.vscode-notification-info .vscode-notification-icon {
    color: #007acc;
}

.vscode-notification-content {
    flex: 1;
    color: #cccccc;
    font-size: 13px;
    line-height: 1.5;
}

.vscode-notification-message {
    word-wrap: break-word;
}

.vscode-notification-close {
    margin-left: 12px;
    cursor: pointer;
    color: #858585;
    font-size: 16px;
    flex-shrink: 0;
    transition: color 0.1s ease;
}

.vscode-notification-close:hover {
    color: #cccccc;
}
</style>